import React, { useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import apiService from "../../services/api-service";
import { Reimbursement } from "../../services/api";

export interface ReimbursementListProps {
  trigger: boolean;
}

const ReimbursementList: React.FC<ReimbursementListProps> = ({ trigger }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reimbursements, setReimbursements] = useState<Reimbursement[]>([]);

  useEffect(() => {
    const fetchReimbursements = async () => {
      if (!user?.username) return;

      try {
        setLoading(true);
        setError(null);
        const data = await apiService.getReimbursementList(user.username);
        setReimbursements(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : t("common.error"));
      } finally {
        setLoading(false);
      }
    };

    fetchReimbursements();
  }, [user?.username, t, trigger]);

  const getStatusColor = (status: string): "success" | "error" | "warning" => {
    switch (status) {
      case "PROCESSED":
        return "success";
      case "REJECTED":
      case "FAILED":
      case "ERROR":
        return "error";
      default:
        return "warning";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("reimbursements.dateSubmitted")}</TableCell>
            <TableCell>{t("reimbursements.items")}</TableCell>
            <TableCell>{t("common.status")}</TableCell>
            <TableCell>{t("reimbursements.attachments")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {reimbursements.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                {t("reimbursements.noRequests")}
              </TableCell>
            </TableRow>
          ) : (
            reimbursements.map((reimbursement, index) => (
              <TableRow key={index}>
                <TableCell>{formatDate(reimbursement.dateSubmitted)}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    {reimbursement.items.map((item, i) => (
                      <Chip key={i} label={item} size="small" />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Tooltip title={reimbursement.errorMsg || ""}>
                    <Chip
                      label={reimbursement.status}
                      color={getStatusColor(reimbursement.status)}
                      size="small"
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Stack
                    direction="column"
                    spacing={1}
                    sx={{
                      alignItems: "flex-start",
                      maxWidth: "100%",
                      pt: 1,
                    }}
                  >
                    {reimbursement.attachments.map((filename, i) => (
                      <Tooltip key={i} title={filename} enterDelay={500}>
                        <Chip
                          label={filename}
                          size="small"
                          variant="outlined"
                          color="info"
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ReimbursementList;
